import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Users, Check, X, Mail, GitCompare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import { formatPrice } from '../utils/formatPrice';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "contact@atlantiquespaboutique.com";

const ComparePage = () => {
  const [searchParams] = useSearchParams();
  const [spas, setSpas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpas = async () => {
      const slugs = searchParams.get('spas')?.split(',') || [];
      if (slugs.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const responses = await Promise.all(
          slugs.slice(0, 3).map(slug => axios.get(`${API}/spas/${slug}`).catch(() => null))
        );
        setSpas(responses.filter(r => r !== null).map(r => r.data));
      } catch (error) {
        console.error('Error fetching spas:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSpas();
  }, [searchParams]);

  // Define comparison rows
  const comparisonRows = [
    { key: 'gamme', label: 'Gamme', getValue: (spa) => spa.gamme || '-' },
    { key: 'places', label: 'Nombre de places', getValue: (spa) => spa.places },
    { key: 'dimensions', label: 'Dimensions', getValue: (spa) => spa.dimensions },
    { key: 'jets', label: 'Nombre de jets', getValue: (spa) => spa.specs?.jets ?? '-' },
    { key: 'pompes', label: 'Pompes', getValue: (spa) => spa.specs?.pompes || '-' },
    { key: 'pompe_circulation', label: 'Pompe de circulation', getValue: (spa) => spa.specs?.pompe_circulation || '-' },
    { key: 'boitier', label: 'Boîtier de contrôle', getValue: (spa) => spa.specs?.boitier || '-' },
    { key: 'clavier', label: 'Clavier de commande', getValue: (spa) => spa.specs?.clavier || '-' },
    { key: 'filtration', label: 'Filtration', getValue: (spa) => spa.specs?.filtration || '-' },
    { key: 'desinfection', label: 'Désinfection', getValue: (spa) => spa.specs?.desinfection || '-' },
    { key: 'eclairage', label: 'Éclairage', getValue: (spa) => spa.specs?.eclairage || '-' },
    { key: 'bluetooth', label: 'Bluetooth', getValue: (spa) => spa.specs?.bluetooth || '-' },
    { key: 'audio', label: 'Système audio', getValue: (spa) => spa.specs?.audio || '-' },
    { key: 'isolation', label: 'Isolation', getValue: (spa) => spa.specs?.isolation || '-' },
    { key: 'lames_nage', label: 'Lames de nage', getValue: (spa) => spa.specs?.lames_nage || '-' },
    { key: 'poids_vide', label: 'Poids à vide', getValue: (spa) => spa.specs?.poids_vide || '-' },
    { key: 'volume', label: 'Volume d\'eau', getValue: (spa) => spa.specs?.volume || '-' },
    { key: 'couleur_coque', label: 'Couleur coque', getValue: (spa) => spa.specs?.couleur_coque || '-' },
    { key: 'couleur_jupe', label: 'Couleur jupe', getValue: (spa) => spa.specs?.couleur_jupe || '-' },
    { key: 'included', label: 'Inclus', getValue: (spa) => spa.included || '-' },
  ];

  // Filter rows that have at least one value
  const activeRows = comparisonRows.filter(row => 
    spas.some(spa => {
      const val = row.getValue(spa);
      return val && val !== '-';
    })
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (spas.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <GitCompare className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Aucun spa à comparer</h1>
          <p className="text-slate-600 mb-8">
            Sélectionnez au moins 2 spas depuis notre catalogue pour les comparer.
          </p>
          <Link to="/spas">
            <Button>Voir le catalogue</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="compare-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/spas">Nos Spas</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Comparateur</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/spas" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Retour au catalogue
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            Comparer les spas
          </h1>
          <p className="text-slate-600">
            Comparez les caractéristiques de {spas.length} spa{spas.length > 1 ? 's' : ''} sélectionné{spas.length > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* Header with spa images and names */}
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="p-4 text-left text-sm font-medium text-slate-600 w-48 min-w-[180px]">
                    Caractéristiques
                  </th>
                  {spas.map((spa) => (
                    <th key={spa.slug} className="p-4 min-w-[250px]">
                      <div className="flex flex-col items-center gap-3">
                        <Link to={`/spas/${spa.slug}`} className="block">
                          <img 
                            src={spa.images?.[0] || 'https://images.unsplash.com/photo-1574186907057-849802af13d9?w=400'} 
                            alt={spa.name}
                            className="w-full h-40 object-contain bg-white rounded-lg hover:opacity-90 transition-opacity p-2"
                          />
                        </Link>
                        {spa.gamme && (
                          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                            Gamme {spa.gamme}
                          </span>
                        )}
                        <Link to={`/spas/${spa.slug}`} className="text-center hover:text-blue-600 transition-colors">
                          <h3 className="font-semibold text-slate-900">{spa.name}</h3>
                        </Link>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Users className="w-4 h-4" />
                          {spa.places} places
                        </div>
                        {spa.price_on_request ? (
                          <span className="text-orange-600 font-semibold">Prix sur devis</span>
                        ) : (
                          <span className="text-xl font-bold text-slate-900">{formatPrice(spa.price)}</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Comparison rows */}
              <tbody>
                {activeRows.map((row, index) => (
                  <tr 
                    key={row.key} 
                    className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                  >
                    <td className="p-4 text-sm font-medium text-slate-700">
                      {row.label}
                    </td>
                    {spas.map((spa) => {
                      const value = row.getValue(spa);
                      const hasValue = value && value !== '-';
                      return (
                        <td key={`${spa.slug}-${row.key}`} className="p-4 text-center">
                          {hasValue ? (
                            <span className="text-sm text-slate-900">{value}</span>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* CTA Section */}
        <div className="mt-8 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">
            Besoin d'aide pour choisir ?
          </h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            Notre équipe spécialiste vous conseille gratuitement pour trouver le spa adapté à votre projet et votre budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`mailto:${EMAIL}`}>
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 gap-2 w-full sm:w-auto">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
            <Link to="/devis">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-slate-800 w-full sm:w-auto">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>

        {/* Installation notice */}
        <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
          <p className="text-sm text-blue-800">
            <strong>Livraison, installation et mise en service</strong> réalisées par un technicien spécialiste, sur devis selon votre configuration.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ComparePage;
